import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Home from "@/pages/Home";
import Profile from "@/pages/Profile";
import Tessera from "@/pages/Tessera";
import Boats from "@/pages/Boats";
import Regate from "@/pages/Regate";
import Mercatino from "@/pages/Mercatino";
import Admin from "@/pages/Admin";
import Verifica from "@/pages/Verifica";
import Privacy from "@/pages/Privacy";
import News from "@/pages/News";
import Comunicazioni from "@/pages/Comunicazioni";
import Messaggi from "@/pages/Messaggi";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthExtra";
import NewsDetail from "@/pages/NewsDetail";
import Landing from "@/pages/Landing";

const Loader = () => (
  <div className="min-h-screen grid place-items-center" data-testid="app-loader">
    <img src="/logo-9er.png" alt="9er" className="h-16 w-16 animate-pulse" />
  </div>
);

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function Root() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Landing />;
  return <Layout><Home /></Layout>;
}

function PublicNews() {
  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <header className="bg-[#0B192C]"><div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center"><a href="/associazione" className="flex items-center gap-3" data-testid="public-news-logo"><img src="/logo-9er.png" alt="Logo" className="h-9 w-9 rounded-md bg-white p-0.5" /><span className="font-heading uppercase text-white">Associazione italiana Classi Nacra-9er</span></a></div></header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12"><NewsDetail publico /></main>
    </div>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user) return <Navigate to={location.pathname === "/registrazione" ? "/tessera" : "/"} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/registrazione" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/password-dimenticata" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verifica-email" element={<VerifyEmail />} />
            <Route path="/messaggi" element={<Protected><Messaggi /></Protected>} />
            <Route path="/" element={<Root />} />
            <Route path="/associazione" element={<Landing />} />
            <Route path="/associazione/news/:id" element={<PublicNews />} />
            <Route path="/profilo" element={<Protected><Profile /></Protected>} />
            <Route path="/tessera" element={<Protected><Tessera /></Protected>} />
            <Route path="/barche" element={<Protected><Boats /></Protected>} />
            <Route path="/regate" element={<Protected><Regate /></Protected>} />
            <Route path="/mercatino" element={<Protected><Mercatino /></Protected>} />
            <Route path="/notizie" element={<Protected><News /></Protected>} />
            <Route path="/notizie/:id" element={<Protected><NewsDetail /></Protected>} />
            <Route path="/comunicazioni" element={<Protected><Comunicazioni /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin", "collaboratore"]}><Admin /></Protected>} />
            <Route path="/verifica/:id" element={<Verifica />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
